<template>
  <header
    class="fixed top-0 left-0 right-0 h-[50px] bg-dark-tertiary border-b border-dark
           flex items-center justify-between px-3 z-50 select-none"
  >
    <!-- Logo -->
    <div class="flex items-center gap-3 min-w-[200px]">
      <div class="w-8 h-8 bg-accent rounded-xl flex items-center justify-center shadow-md flex-shrink-0 logo-gradient">
        <Play class="w-4 h-4 text-white" />
      </div>
      <div class="flex flex-col leading-tight">
        <h1 class="text-[14px] font-bold text-dark-primary tracking-tight">SeguraWatch</h1>
        <p class="text-[10px] text-dark-secondary font-medium">AI Forensic Auditing</p>
      </div>
    </div>

    <!-- Global Search -->
    <div class="flex-1 max-w-2xl mx-auto">
      <GlobalSearch />
    </div>

    <!-- Actions -->
    <div class="min-w-[220px] flex items-center justify-end gap-2">

      <button
        v-if="!auth.user"
        @click="loginWithGoogle"
        class="px-3 py-1.5 rounded-md text-sm font-medium bg-accent text-white hover:opacity-90 transition"
      >
        Login
      </button>

      <template v-else>

        <!-- Theme Toggle -->
        <button
          @click="toggleTheme"
          class="w-8 h-8 flex items-center justify-center rounded-lg hover-bg transition"
          :title="`Switch to ${currentTheme === 'dark' ? 'Light' : 'Dark'} theme`"
        >
          <Sun  v-if="currentTheme === 'dark'" class="theme-icon text-dark-secondary w-5 h-5" />
          <Moon v-else                          class="theme-icon text-dark-secondary w-5 h-5" />
        </button>

        <!-- Bell notifications -->
        <div class="relative bell-container">
          <button
            @click="toggleBell"
            class="w-8 h-8 relative flex items-center justify-center rounded-lg hover-bg transition text-dark-secondary"
            title="Notifications"
          >
            <Bell class="w-5 h-5" />
            <span
              v-if="pendingInvites.length + auth.notifications.length > 0"
              class="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 text-white text-[10px] font-bold rounded-full flex items-center justify-center"
              style="background: #f59e0b;"
            >
              {{ Math.min(pendingInvites.length + auth.notifications.length, 9) }}{{ pendingInvites.length + auth.notifications.length > 9 ? '+' : '' }}
            </span>
          </button>

          <transition name="menu-fade">
            <div
              v-if="showBell"
              class="absolute right-0 mt-3 w-80 bg-dark-tertiary border border-dark rounded-xl shadow-xl overflow-hidden z-50"
            >
              <div class="px-4 py-3 border-b border-dark">
                <p class="text-sm font-bold text-dark-primary">Notifications</p>
              </div>

              <div v-if="pendingInvites.length === 0 && auth.notifications.length === 0" class="px-4 py-6 text-center">
                <p class="text-sm text-dark-secondary">No notifications</p>
              </div>

              <div v-else>
                <!-- Invite notifications -->
                <button
                  v-for="inv in pendingInvites" :key="inv.id"
                  @click="navigateTo('settings'); showBell = false"
                  class="w-full p-4 flex items-start gap-3 border-b border-dark last:border-0 hover:bg-dark-quaternary transition text-left"
                >
                  <div class="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5" style="background: rgba(var(--accent-rgb), 0.1);">
                    <span class="text-xs font-bold" style="color: var(--accent);">{{ inv.tenantId[0].toUpperCase() }}</span>
                  </div>
                  <div class="flex-1 min-w-0">
                    <p class="text-sm font-semibold text-dark-primary">Workspace invite</p>
                    <p class="text-xs text-dark-secondary mt-0.5">
                      Invited to <span class="font-mono text-dark-primary">{{ inv.tenantId }}</span> as <span class="font-semibold">{{ inv.role }}</span>
                    </p>
                  </div>
                </button>

                <!-- Admin notifications -->
                <button
                  v-for="notif in auth.notifications" :key="notif.id"
                  @click="navigateTo('settings'); showBell = false; markNotifRead(notif.id)"
                  class="w-full p-4 flex items-start gap-3 border-b border-dark last:border-0 hover:bg-dark-quaternary transition text-left"
                >
                  <div class="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
                    :style="notif.type === 'invite_accepted'
                      ? 'background: rgba(var(--accent-rgb),0.1)'
                      : 'background: rgba(239,68,68,0.1)'"
                  >
                    <Check v-if="notif.type === 'invite_accepted'" class="w-4 h-4" style="color: var(--accent);" />
                    <LogOut v-else-if="notif.type === 'member_left'" class="w-4 h-4" style="color: #ef4444;" />
                    <X v-else class="w-4 h-4" style="color: #ef4444;" />
                  </div>
                  <div class="flex-1 min-w-0">
                    <p class="text-sm font-semibold text-dark-primary">
                      {{ notif.type === 'invite_accepted' ? 'Invite accepted' : notif.type === 'member_left' ? 'Member left' : 'Invite declined' }}
                    </p>
                    <p class="text-xs text-dark-secondary mt-0.5">{{ notif.email }}</p>
                  </div>
                </button>
              </div>
            </div>
          </transition>
        </div>

        <!-- User Menu -->
        <div class="relative user-menu-container">
          <button
            @click="toggleMenu"
            class="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full overflow-hidden transition"
            :title="auth.user.email"
          >
            <img
              v-if="auth.user.photoURL && !photoError"
              :src="auth.user.photoURL"
              class="w-8 h-8 rounded-full block"
              @error="photoError = true"
            />
            <div v-else class="w-8 h-8 rounded-full flex items-center justify-center" style="background: rgba(var(--accent-rgb), 0.2);">
              <span class="text-xs font-bold" style="color: var(--accent);">{{ userInitial }}</span>
            </div>
          </button>

          <!-- Dropdown -->
          <transition name="menu-fade">
            <div
              v-if="showMenu"
              class="absolute right-0 mt-3 w-72 bg-dark-tertiary border border-dark rounded-xl shadow-xl overflow-hidden z-50"
            >
              <!-- User Info -->
              <div class="p-4 border-b border-dark">
                <p class="text-sm text-dark-primary font-semibold truncate">{{ auth.user.displayName }}</p>
                <p class="text-xs text-dark-secondary truncate">{{ auth.user.email }}</p>
              </div>

              <!-- Workspaces -->
              <div v-if="auth.tenants && auth.tenants.length > 1" class="border-b border-dark">
                <p class="text-[10px] font-bold text-dark-tertiary uppercase tracking-wider px-4 pt-3 pb-1">Workspaces</p>
                <button
                  v-for="t in auth.tenants"
                  :key="t.tenantId"
                  @click="switchTenant(t.tenantId)"
                  class="menu-item w-full"
                >
                  <div class="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0" style="background: rgba(var(--accent-rgb), 0.2);">
                    <span class="text-xs font-bold" style="color: var(--accent);">{{ t.tenantId[0].toUpperCase() }}</span>
                  </div>
                  <span class="flex-1 text-sm font-medium text-dark-primary truncate text-left">{{ t.tenantId }}</span>
                  <span v-if="t.role === 'admin'"  class="text-[10px] font-bold px-1.5 py-0.5 rounded" style="background: rgba(var(--accent-rgb),.1); color: var(--accent);">ADMIN</span>
                  <span v-else-if="t.role === 'editor'" class="text-[10px] font-bold px-1.5 py-0.5 rounded" style="background: rgba(245,158,11,.1); color: #f59e0b;">EDITOR</span>
                  <span v-else-if="t.role === 'viewer'" class="text-[10px] font-bold px-1.5 py-0.5 rounded" style="background: var(--bg-quaternary); color: var(--text-secondary);">VIEWER</span>
                  <Check class="w-4 h-4 flex-shrink-0" :class="t.tenantId === auth.tenantId ? 'text-accent' : 'opacity-0'" />
                </button>
              </div>

              <!-- Single workspace -->
              <div v-else-if="auth.tenantId" class="border-b border-dark">
                <div class="menu-item cursor-default">
                  <div class="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0" style="background: rgba(var(--accent-rgb), 0.2);">
                    <span class="text-xs font-bold" style="color: var(--accent);">{{ auth.tenantId[0].toUpperCase() }}</span>
                  </div>
                  <span class="flex-1 text-sm font-medium text-dark-primary truncate">{{ auth.tenantId }}</span>
                  <span v-if="auth.role === 'admin'"  class="text-[10px] font-bold px-1.5 py-0.5 rounded" style="background: rgba(var(--accent-rgb),.1); color: var(--accent);">ADMIN</span>
                  <span v-else-if="auth.role === 'editor'" class="text-[10px] font-bold px-1.5 py-0.5 rounded" style="background: rgba(245,158,11,.1); color: #f59e0b;">EDITOR</span>
                  <span v-else-if="auth.role === 'viewer'" class="text-[10px] font-bold px-1.5 py-0.5 rounded" style="background: var(--bg-quaternary); color: var(--text-secondary);">VIEWER</span>
                </div>
              </div>

              <!-- Menu Items -->
              <button @click="navigateTo('settings')" class="menu-item">
                <Settings class="w-4 h-4 text-dark-secondary" />
                <span>Settings</span>
              </button>

              <button @click="navigateToExternal('https://segura0.ai/docs')" class="menu-item">
                <HelpCircle class="w-4 h-4 text-dark-secondary" />
                <span>Help</span>
              </button>

              <div class="border-t border-dark mx-2 my-1"></div>

              <button @click="handleLogout" class="menu-item text-error">
                <LogOut class="w-4 h-4" />
                <span>Logout</span>
              </button>

            </div>
          </transition>
        </div>

      </template>
    </div>
  </header>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from "vue";
import { useRouter } from "vue-router";
import { useThemeStore } from "../stores/themeStore.js";
import { useUIStore } from "../stores/uiStore.js";
import { useAuthStore } from "../stores/authStore";
import { useSessionStore } from "../stores/sessionStore";
import { loginWithGoogle, logoutUser, resolveTenant, _acceptInvitePublic } from "../services/authService";
import GlobalSearch from "./GlobalSearch.vue";
import { Play, Sun, Moon, LogOut, Settings, HelpCircle, Bell, Check, X } from "lucide-vue-next";
import { collection, query, where, onSnapshot, deleteDoc, doc, updateDoc } from "firebase/firestore";
import { db } from "../firebase";

const auth         = useAuthStore();
const sessionStore = useSessionStore();
const router       = useRouter();
const themeStore   = useThemeStore();
const ui           = useUIStore();

const currentTheme   = ref(themeStore.theme);
const showMenu       = ref(false);
const showBell       = ref(false);
const pendingInvites = ref([]);
const photoError     = ref(false);

const userInitial = computed(() => (auth.user?.email ?? "?")[0].toUpperCase());

let unsubscribeInvites = null;

function startInviteListener() {
  if (!auth.user?.email) return;
  const q = query(
    collection(db, "invites"),
    where("email",  "==", auth.user.email),
    where("status", "==", "pending")
  );
  unsubscribeInvites = onSnapshot(q, (snap) => {
    pendingInvites.value = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  });
}

async function acceptInvite(inv) {
  showBell.value = false;
  await _acceptInvitePublic(inv, auth.user.uid, auth.user.email);
  const result = await resolveTenant(auth.user.uid, auth.user.email);
  if (result) auth.setTenant(result);
}

async function declineInvite(inv) {
  await deleteDoc(doc(db, "invites", inv.email));
  await deleteDoc(doc(db, "tenants", inv.tenantId, "invites", inv.email)).catch(() => {});
}

async function markNotifRead(notifId) {
  if (!auth.tenantId) return;
  await updateDoc(doc(db, "tenants", auth.tenantId, "notifications", notifId), { read: true });
}

function toggleMenu() { showMenu.value = !showMenu.value; showBell.value = false; }
function toggleBell()  { showBell.value = !showBell.value; showMenu.value = false; }

function navigateTo(routeName) { showMenu.value = false; router.push({ name: routeName }); }
function navigateToExternal(url) { showMenu.value = false; window.open(url, "_blank"); }

function handleClickOutside(event) {
  if (showMenu.value && !event.target.closest(".user-menu-container")) showMenu.value = false;
  if (showBell.value  && !event.target.closest(".bell-container"))     showBell.value  = false;
}

async function handleLogout() {
  try {
    await logoutUser();
    auth.clearUser();
    if (unsubscribeInvites) unsubscribeInvites();
    showMenu.value = false;
    router.push("/login");
  } catch (error) {
    console.error("Logout error:", error);
  }
}

async function switchTenant(tenantId) {
  if (tenantId === auth.tenantId) { showMenu.value = false; return; }
  showMenu.value = false;
  const tenant = await resolveTenant(auth.user.uid, auth.user.email, tenantId);
  if (tenant) {
    auth.setTenant(tenant);
    sessionStore.loadSessions();
  }
}

function toggleTheme() {
  const newTheme = themeStore.theme === "dark" ? "light" : "dark";
  themeStore.setTheme(newTheme);
  currentTheme.value = newTheme;
}

onMounted(() => {
  currentTheme.value = themeStore.theme;
  document.addEventListener("click", handleClickOutside);
  if (auth.user?.email) startInviteListener();
});

watch(() => auth.user, (user) => {
  if (unsubscribeInvites) { unsubscribeInvites(); unsubscribeInvites = null; }
  if (user?.email) startInviteListener();
});

onUnmounted(() => {
  document.removeEventListener("click", handleClickOutside);
  if (unsubscribeInvites) unsubscribeInvites();
});
</script>

<style scoped>
.user-menu-container { padding-right: 0.75rem; }

.menu-item {
  width: 100%;
  padding: 0.625rem 1rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-primary);
  background: transparent;
  border: none;
  cursor: pointer;
  transition: background 0.15s ease;
}
.menu-item:hover { background: var(--bg-quaternary); }

.menu-fade-enter-active, .menu-fade-leave-active { transition: all 0.2s ease; }
.menu-fade-enter-from, .menu-fade-leave-to { opacity: 0; transform: translateY(-5px); }

header { backdrop-filter: blur(8px); -webkit-backdrop-filter: blur(8px); }

.logo-gradient {
  background: linear-gradient(135deg, var(--accent) 0%, rgba(var(--accent-rgb), 0.8) 100%);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}
.logo-gradient:hover { transform: scale(1.05); box-shadow: 0 4px 12px rgba(var(--accent-rgb), 0.4); }

.theme-icon { transition: all 0.2s ease; }
button:hover .theme-icon { color: var(--text-primary); transform: rotate(20deg); }

@media print { header { display: none !important; } }
</style>